// TV1-1782 ticket 12 — FE capability gates.
//
// This module exposes two thin hook wrappers ( `useCapability`, `useCan` )
// that read the authenticated user from the auth context ( T11 hydrated
// the profile with `capabilities`, `scopesByCapability` and
// `assignedScopes` ).
//
// The isomorphic `can()` evaluator now lives in `@bett3r-dev/teselly-capabilities`
// ( moved by T24 ) so BE and FE share a single implementation. It is
// re-exported here for callers that already import from this path.
//
// Isomorphism contract ( SDD §7.1 ): for the same ( user, key, instance )
// triple the FE answer MUST match the backend's `scopeInvariant` /
// `FilterByScopeTransformer` decision. The shared `Resources` predicates
// from `@bett3r-dev/teselly-capabilities` are the single source of truth — never
// re-implement scope semantics here.
import { can as sharedCan } from '@bett3r-dev/teselly-capabilities';
import type { CapabilityKey, ScopeLevel } from '@bett3r-dev/teselly-capabilities';
import useAuth, { User } from '@contexts/useAuth/useAuth';

// The authenticated user as projected onto `/users-profiles/me` carries the
// auth id under `id`. The shared `CanUser` shape ( consumed by the
// predicates in `Resources` ) names this field `userId` because that's how
// scopes are anchored on the backend. We translate here at the FE boundary.
export type CanUser = Pick<User, 'id' | 'assignedScopes' | 'scopesByCapability'>;

const toSharedUser = ( user: CanUser ) => ({
  userId: user.id,
  assignedScopes: user.assignedScopes ?? [],
  scopesByCapability: user.scopesByCapability ?? {}
});

/**
 * Isomorphic capability check — see `@bett3r-dev/teselly-capabilities`.`can` for
 * the contract. This FE wrapper translates the FE `User.id` field onto
 * the shared `userId` field expected by `Resources` predicates.
 */
export function can(
  user: CanUser | null | undefined,
  key: CapabilityKey,
  instance?: Record<string, unknown>
): boolean {
  if ( !user ) return false;
  return sharedCan( toSharedUser( user ), key, instance );
}

/**
 * ANY-OF capability check. `true` when `keys` is empty ( nothing to gate on ) or
 * the user holds at least one of them. Used by the section-level gates, which
 * must admit every capability one of the section's tabs is gated on — see
 * `sectionGateCapabilities` in `@config/navigation.config`. [TV1-2259]
 */
export function canAny(
  user: CanUser | null | undefined,
  keys: CapabilityKey[],
  instance?: Record<string, unknown>
): boolean {
  if ( keys.length === 0 ) return true;
  return keys.some( key => can( user, key, instance ));
}

/**
 * Reactive capability lookup. Returns the user's scopes for `key` plus a
 * convenience `has` boolean ( true iff at least one scope is granted ).
 * No instance evaluation here — use `useCan( key, instance )` for that.
 */
export function useCapability( key: CapabilityKey ): { has: boolean; scopes: ScopeLevel[] } {
  const auth = useAuth();
  const scopes = auth?.user?.scopesByCapability?.[ key ] ?? [];
  return { has: scopes.length > 0, scopes };
}

/**
 * Reactive isomorphic capability check. Thin wrapper that pulls the user
 * out of context and delegates to `can()`.
 */
export function useCan( key: CapabilityKey, instance?: Record<string, unknown> ): boolean {
  const auth = useAuth();
  return can( auth?.user ?? null, key, instance );
}
